import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Flex,
  Tag,
  message,
  Card,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { fetchGames, createGame, updateGame, deleteGame, toggleGameStatus, importGames } from '../../utils/api';
import { downloadGameTemplate } from '../../utils/csvTemplate';

const { TextArea } = Input;
const { Option } = Select;

const GameManagement = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [formData, setFormData] = useState({
    cName: '',
    eName: '',
    aliases: '',
    status: 1
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  // 批量导入
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    loadGames();
  }, [currentPage, pageSize]);

  // 初始化时加载跨页统计；以及操作成功后手动触发


  const loadGames = async () => {
    try {
      setLoading(true);
      const data = await fetchGames(currentPage, pageSize);
      setGames(data.list || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('加载游戏数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGame(null);
    setFormData({ cName: '', eName: '', aliases: '', status: 1 });
    setShowModal(true);
  };

  const openImportModal = () => {
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportModalVisible(true);
  };

  const closeImportModal = () => {
    setImportModalVisible(false);
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportLoading(false);
  };

  const normalizeStatus = (val) => {
    const s = String(val || '').trim().toLowerCase();
    if (['1', 'enabled', 'active', 'true', '启用'].includes(s)) return 1;
    if (['0', 'disabled', 'inactive', 'false', '禁用'].includes(s)) return 0;
    return 1;
  };

  const parseCsv = (text) => {
    const errors = [];
    const rows = [];
    // 去除可能的 BOM 并按行拆分
    const stripBom = (s) => String(s || '').replace(/^\ufeff/, '');
    const lines = stripBom(String(text || '')).trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return { rows, errors };

    const firstCols = lines[0].split(',').map(c => stripBom(c).trim());
    const lcCols = firstCols.map(c => c.toLowerCase());
    // 兼容中英文表头：cName/eName/status 或 中文名/英文名/状态 等
    const containsCn = lcCols.some(c => c.includes('cname') || c.includes('中文'));
    const containsEn = lcCols.some(c => c.includes('ename') || c.includes('英文'));
    const containsStatus = lcCols.some(c => c.includes('status') || c.includes('状态'));
    const hasHeader = (containsCn && containsEn) || ((containsCn || containsEn) && containsStatus);
    const startIdx = hasHeader ? 1 : 0;
    for (let i = startIdx; i < lines.length; i++) {
      const raw = lines[i];
      const cols = raw.split(',').map(c => stripBom(c).trim());
      const cName = cols[0] || '';
      const eName = cols[1] || '';
      const status = normalizeStatus(cols[2] || 'enabled');
      if (!cName || !eName) {
        errors.push(`第 ${i + 1} 行：cName/eName 不能为空`);
        continue;
      }
      rows.push({ cName, eName, status });
    }
    return { rows, errors };
  };

  const handleCsvTextChange = (e) => {
    const text = e.target.value;
    setCsvText(text);
    const { rows, errors } = parseCsv(text);
    setImportRows(rows);
    setImportErrors(errors);
  };

  // 通用：从文件读取文本，自动尝试 UTF-8 / GBK / GB18030，修复乱码
  const decodeCsvFile = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const tryDecode = (label) => {
        try {
          return new TextDecoder(label, { fatal: false }).decode(buffer);
        } catch (e) {
          return null;
        }
      };
      let text = tryDecode('utf-8');
      const hasReplacement = (s) => (s.match(/\uFFFD/g) || []).length > 0 || /����/.test(s);
      const hasChinese = (s) => /[\u4e00-\u9fa5]/.test(s);
      if (!text || hasReplacement(text)) {
        text = tryDecode('gbk') || tryDecode('gb18030') || text || '';
      }
      // 如果仍无中文且存在替换字符，最后兜底使用 utf-8
      if ((!hasChinese(text)) && hasReplacement(text)) {
        text = tryDecode('utf-8') || text;
      }
      return text || '';
    } catch (err) {
      console.error('读取文件失败: ', err);
      message.error('读取文件失败');
      return '';
    }
  };

  // 参照静态HTML的拖拽上传区域：支持拖拽/点击选择CSV文件，最大10MB
  const handleCsvFileUpload = (file) => {
    if (!file) return false;
    const isCsv = file.type === 'text/csv' || /\.csv$/i.test(file.name);
    if (!isCsv) {
      message.error('请上传 CSV 文件');
      return Upload.LIST_IGNORE || false;
    }
    const isLt10M = file.size <= 10 * 1024 * 1024; // 10MB
    if (!isLt10M) {
      message.error('CSV 文件大小不能超过 10MB');
      return Upload.LIST_IGNORE || false;
    }
    // 异步读取并解析（返回 false 阻止真实上传）
    (async () => {
      const text = await decodeCsvFile(file);
      setCsvText(text);
      const { rows, errors } = parseCsv(text);
      setImportRows(rows);
      setImportErrors(errors);
    })();
    // 阻止 antd 执行真实上传
    return false;
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) {
      message.warning('没有可导入的数据');
      return;
    }
    setImportLoading(true);
    const result = await importGames(importRows);
    setImportLoading(false);
    if (!result?.success) {
      message.error(result?.message || '导入失败');
      return;
    }
    message.success(result?.message || `导入成功（${importRows.length} 条）`);
    closeImportModal();
    await loadGames();
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      cName: game.cName || '',
      eName: game.eName || '',
      aliases: Array.isArray(game.aliases) ? game.aliases.join(',') : '',
      status: game.status ? 1 : 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const aliasArray = (formData.aliases || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      console.log('handleSave called with formData:', formData, 'aliasArray:', aliasArray);
      const payload = {
        cName: formData.cName,
        eName: formData.eName,
        aliases: aliasArray,
        status: Number(formData.status) === 1 ? 1 : 0
      };
      if (editingGame) {
        await updateGame(editingGame.id, payload);
        message.success('游戏更新成功');
      } else {
        await createGame(payload);
        message.success('游戏创建成功');
      }
      setShowModal(false);
      await loadGames();
    } catch (error) {
      console.error('保存游戏数据失败:', error);
      message.error('保存失败，请重试');
    }
  };

  const handleDelete = (gameId) => {
    console.log('handleDelete called with gameId:', gameId);
    setGameToDelete(gameId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    console.log('confirmDelete called with gameId:', gameToDelete);
    try {
      await deleteGame(gameToDelete);
      message.success('删除成功');
      await loadGames();
    } catch (error) {
      console.error('删除游戏失败:', error);
      message.error('删除失败，请重试');
    } finally {
      setDeleteModalVisible(false);
      setGameToDelete(null);
    }
  };

  const cancelDelete = () => {
    console.log('Delete cancelled');
    setDeleteModalVisible(false);
    setGameToDelete(null);
  };

  const handleToggleStatus = async (game) => {
    try {
      const result = await toggleGameStatus(game.id);
      // 使用API返回的最新数据更新本地状态
      setGames(prev => prev.map(g =>
        g.id === game.id
          ? { ...g, status: result.status }
          : g
      ));
      message.success(`游戏已${result.status === 1 ? '启用' : '禁用'}`);
    } catch (error) {
      console.error('更新游戏状态失败:', error);
      message.error('状态切换失败，请重试');
    }
  };
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '中文名',
      dataIndex: 'cName',
      key: 'cName',
    },
    {
      title: '英文名',
      dataIndex: 'eName',
      key: 'eName',
      render: (text) => text || '-',
    },
    {
      title: '别名',
      dataIndex: 'aliases',
      key: 'aliases',
      render: (aliases) => {
        const arr = Array.isArray(aliases)
          ? aliases
          : (typeof aliases === 'string'
            ? aliases.split(',').map(s => s.trim()).filter(Boolean)
            : []);
        if (!arr || arr.length === 0) {
          return <span style={{ color: '#999' }}>未设置</span>;
        }

        /* 随机颜色池 */
        const colorPool = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple'];
        const randomColor = () => colorPool[Math.floor(Math.random() * colorPool.length)];

        return (
          <Space wrap size="small">
            {arr.map((alias, idx) => (
              <Tag color={randomColor()} key={`${alias}-${idx}`}>
                {alias}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date || Date.now()).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type={record.status === 1 ? 'default' : 'primary'}
            size="small"
            icon={record.status === 1 ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="管理游戏数据">
        <Flex wrap gap="small" style={{marginBottom: '16px'}}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            新增游戏
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={openImportModal}
          >
            批量导入
          </Button>
        </Flex>
        <Table
          columns={columns}
          dataSource={games}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: totalCount,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </Card>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入游戏"
        open={importModalVisible}
        onCancel={closeImportModal}
        onOk={handleImportSubmit}
        okText="导入并保存"
        confirmLoading={importLoading}
        width={700}
      >
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Button type="link" onClick={downloadGameTemplate}>
              下载CSV模板
            </Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* 拖拽上传区域（参照静态HTML设计） */}
            <Upload.Dragger
              accept=".csv"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleCsvFileUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">拖放 CSV 文件到此处，或点击上传</p>
              <p className="ant-upload-hint">支持的格式: .csv（最大 10MB）</p>
            </Upload.Dragger>

            {/* CSV 格式说明与示例 */}
            <div>
              <p style={{ color: '#555', marginTop: 8, marginBottom: 4 }}>CSV 文件格式要求：</p>
              <ul style={{ paddingLeft: 18, color: '#666' }}>
                <li>第一行为表头: cName,eName,status（可选）</li>
                <li>状态支持: 启用/禁用 或 enabled/disabled 或 1/0</li>
                <li>示例: 英雄联盟,League of Legends,启用</li>
              </ul>
            </div>

            {/* 粘贴CSV内容 */}
            <div>
              <p style={{ color: '#666', marginBottom: 8 }}>或直接粘贴CSV内容（包含或不包含表头）：cName,eName,status</p>
              <TextArea rows={6} value={csvText} onChange={handleCsvTextChange} placeholder="示例：\n示例游戏,Demo Game,enabled\n王者荣耀,Honor of Kings,enabled" />
            </div>
          </Space>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Card size="small" title={`预览（有效 ${importRows.length} 条${importErrors.length ? `，错误 ${importErrors.length} 条` : ''}）`}>
            {importErrors.length > 0 && (
              <div style={{ color: '#c00', marginBottom: 8 }}>
                {importErrors.slice(0, 5).map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
                {importErrors.length > 5 && <div>更多错误已隐藏...</div>}
              </div>
            )}
            <Table
              size="small"
              columns={[
                { title: '中文名', dataIndex: 'cName' },
                { title: '英文名', dataIndex: 'eName' },
                { title: '状态', dataIndex: 'status', render: (s) => (s === 1 ? '启用' : '禁用') }
              ]}
              dataSource={importRows.map((r, i) => ({ ...r, _key: i }))}
              rowKey="_key"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      </Modal>

      {/* 模态框 */}
      <Modal
        title={editingGame ? '编辑游戏' : '新增游戏'}
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item label="游戏中文名" required>
            <Input
              value={formData.cName}
              onChange={(e) => setFormData({ ...formData, cName: e.target.value })}
              placeholder="请输入中文名"
            />
          </Form.Item>

          <Form.Item label="英文名">
            <Input
              value={formData.eName}
              onChange={(e) => setFormData({ ...formData, eName: e.target.value })}
              placeholder="请输入英文名"
            />
          </Form.Item>

          {editingGame && <Form.Item label="别名">
            <TextArea
              value={formData.aliases}
              onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
              placeholder="逗号分隔，如：gta5, grandtheftauto5"
              rows={3}
            />
          </Form.Item>}

          <Form.Item label="状态">
            <Select
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value })}
              style={{ width: '100%' }}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 删除确认模态框 */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="确定"
        cancelText="取消"
        okType="danger"
      >
        <p>确定要删除这个游戏吗？</p>
      </Modal>
    </div>
  );
};

export default GameManagement;